import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Proposals from "@/pages/Proposals";
import Editor from "@/pages/Editor";
import Clients from "@/pages/Clients";
import BrandKits from "@/pages/BrandKits";
import Templates from "@/pages/Templates";
import AIStudio from "@/pages/AIStudio";
import Settings from "@/pages/Settings";
import PublicProposal from "@/pages/PublicProposal";
import Landing from "@/pages/Landing";
import GoogleCallback from "@/pages/GoogleCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { About, Contact, Privacy, Terms, Refund } from "@/pages/InfoPages";
import Plans from "@/pages/Plans";
import MetaLanding from "@/pages/MetaLanding";
import LifetimeDeal from "@/pages/LifetimeDeal";
import Checkout from "@/pages/Checkout";
import PaymentStatus from "@/pages/PaymentStatus";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLive from "@/pages/admin/AdminLive";
import AdminMonitoring from "@/pages/admin/AdminMonitoring";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminProposals from "@/pages/admin/AdminProposals";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminAI from "@/pages/admin/AdminAI";
import AdminAppSumo from "@/pages/admin/AdminAppSumo";
import OpsSignin from "@/pages/OpsSignin";
import VerifyEmail from "@/pages/VerifyEmail";
import { AdminProvider, OpsThemeProvider, T, useAdmin } from "@/components/admin/ui";
import { ADMIN_SLUG } from "@/lib/adminPath";
import { usePresence } from "@/hooks/usePresence";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { consumeTokenHandoff } from "@/lib/appUrl";
import { MetaRouteTracking } from "@/components/MetaRouteTracking";
import { OaiRouteTracking } from "@/components/OaiRouteTracking";

const ADMIN_PAGES = [
  ["", AdminDashboard],
  ["live", AdminLive],
  ["monitoring", AdminMonitoring],
  ["users", AdminUsers],
  ["proposals", AdminProposals],
  ["payments", AdminPayments],
  ["appsumo", AdminAppSumo],
  ["ai", AdminAI],
  ["audit", AdminAudit],
];

const AppRoutes = () => {
  usePresence();
  return (
    <Routes>
      {/* MetaLanding is now the public home page; the previous landing stays at /home */}
      <Route path="/" element={<MetaLanding />} />
      <Route path="/home" element={<Landing />} />
      {/* paid-traffic landing page: direct URL only, never linked from the nav */}
      <Route path="/meta-proposals" element={<MetaLanding />} />
      <Route path="/lifetime-deal" element={<LifetimeDeal />} />
      <Route path="/p/:token" element={<PublicProposal />} />
      <Route path="/login" element={<Guest><Login /></Guest>} />
      <Route path="/signup" element={<Guest><Signup /></Guest>} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refund-policy" element={<Refund />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/ops/signin" element={<OpsSignin />} />
      <Route path="/plans" element={<Protected><Plans /></Protected>} />
      <Route path="/checkout" element={<Protected><Checkout /></Protected>} />
      <Route path="/payment/:orderId" element={<Protected><PaymentStatus /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/proposals" element={<Protected><Proposals /></Protected>} />
      <Route path="/editor/:id" element={<Protected><Editor /></Protected>} />
      <Route path="/clients" element={<Protected><Clients /></Protected>} />
      <Route path="/brand" element={<Protected><BrandKits /></Protected>} />
      <Route path="/templates" element={<Protected><Templates /></Protected>} />
      <Route path="/ai" element={<Protected><AIStudio /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      {ADMIN_SLUG &&
        ADMIN_PAGES.map(([sub, Page]) => (
          <Route
            key={sub || "root"}
            path={`/${ADMIN_SLUG}/admin${sub ? `/${sub}` : ""}`}
            element={
              <AdminOnly>
                <Page />
              </AdminOnly>
            }
          />
        ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const OpsSplash = ({ children }) => (
  <div className={`${T.page} grid place-items-center px-6`}>{children}</div>
);

const AdminGate = ({ children }) => {
  const { state } = useAdmin();
  const { user, logout } = useAuth();
  if (state === "loading")
    return (
      <OpsSplash>
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600 dark:text-emerald-400" />
      </OpsSplash>
    );
  if (state === "denied") {
    const switchAccount = async () => {
      await logout();
      window.location.href = "/ops/signin";
    };
    return (
      <OpsSplash>
        <div className="max-w-md text-center space-y-4" data-testid="admin-denied">
          <p className="text-[11px] uppercase tracking-[0.16em] font-medium text-emerald-600 dark:text-emerald-400">
            403 · restricted
          </p>
          <h1 className="text-xl font-semibold tracking-tight">Ops console access required</h1>
          <p className={`text-sm ${T.muted}`}>
            This area is limited to Propojo platform staff.
          </p>
          {user?.email && (
            <p className="text-sm" data-testid="admin-denied-current-account">
              You are signed in as{" "}
              <span className="font-medium text-slate-900 dark:text-slate-100">{user.email}</span>, which has no
              platform role. If that is not your staff account, switch below.
            </p>
          )}
          <div className="flex flex-wrap gap-2 justify-center pt-1">
            <button data-testid="admin-denied-switch-account" onClick={switchAccount} className={`${T.btn} ${T.btnPrimary}`}>
              Sign out & staff sign in
            </button>
            <a href="/dashboard" data-testid="admin-denied-back" className={`${T.btn} ${T.btnGhost}`}>
              Back to app
            </a>
          </div>
        </div>
      </OpsSplash>
    );
  }
  return children;
};

const AdminOnly = ({ children }) => {
  const { user, loading } = useAuth();
  return (
    <OpsThemeProvider>
      {loading ? (
        <OpsSplash>
          <Loader2 className="w-6 h-6 animate-spin text-emerald-600 dark:text-emerald-400" />
        </OpsSplash>
      ) : !user ? (
        <Navigate to="/ops/signin" replace />
      ) : (
        <AdminProvider>
          <AdminGate>{children}</AdminGate>
        </AdminProvider>
      )}
    </OpsThemeProvider>
  );
};

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-app text-zinc-600">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  return user ? children : <Navigate to="/login" replace />;
};

const Guest = ({ children }) => {
  const { user, loading } = useAuth();
  // A fresh signup wants the plan picker, not the dashboard. Read on redirect, clear in an effect
  // (mutating storage during render makes React's double-render lose the value).
  const intended = user ? sessionStorage.getItem("pj_post_auth") : null;
  useEffect(() => {
    if (user) sessionStorage.removeItem("pj_post_auth");
  }, [user]);
  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-app text-zinc-600">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  if (!user) return children;
  return <Navigate to={intended || "/dashboard"} replace />;
};

export default function App() {
  // A `#t=` fragment is a one-time session handoff from the marketing host to the app host.
  consumeTokenHandoff();
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <MetaRouteTracking />
          <OaiRouteTracking />
          <ThemedToaster />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

const ThemedToaster = () => {
  const { resolved } = useTheme();
  return <Toaster position="bottom-right" theme={resolved} richColors />;
};
